import { useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import { ChevronsUpDown, Loader2 } from "lucide-react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { apiFetch } from "@/lib/api-client";
import { FieldShell, type FieldComponentProps } from "./field-shell";

type Hit = { id: string; company_name: string };

export function PartnerLookupField({
  field,
  control,
  disabled,
  embedContext,
}: FieldComponentProps) {
  if (field.kind !== "partner_lookup") return null;

  // Public embed has no session → /api/partners/lookup would 401. Degrade
  // to a free-text email input; the receiving end stores it on
  // form_submissions.submitter_email for internal review. The partner
  // portal page never passes embedContext, so this branch is for the
  // future public embed surface (Phase 7 sub-bucket 19).
  if (embedContext) {
    return (
      <Controller
        name={field.name}
        control={control}
        render={({ field: f, fieldState }) => (
          <FieldShell
            id={f.name}
            label={field.label}
            required={field.required}
            helpText={
              field.helpText ??
              "Enter the partner contact email — Bookboost will match it on review."
            }
            error={fieldState.error?.message}
          >
            <Input
              id={f.name}
              type="email"
              value={f.value == null ? "" : String(f.value)}
              onChange={(e) => f.onChange(e.target.value)}
              onBlur={f.onBlur}
              placeholder="partner@example.com"
              disabled={disabled}
              aria-invalid={!!fieldState.error}
            />
          </FieldShell>
        )}
      />
    );
  }

  return (
    <Controller
      name={field.name}
      control={control}
      render={({ field: f, fieldState }) => (
        <FieldShell
          id={f.name}
          label={field.label}
          required={field.required}
          helpText={field.helpText}
          error={fieldState.error?.message}
        >
          <PartnerCombo
            value={typeof f.value === "string" ? f.value : null}
            onChange={(id) => f.onChange(id)}
            disabled={disabled}
            error={!!fieldState.error}
          />
        </FieldShell>
      )}
    />
  );
}

function PartnerCombo({
  value,
  onChange,
  disabled,
  error,
}: {
  value: string | null;
  onChange: (id: string | null) => void;
  disabled?: boolean;
  error?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [hits, setHits] = useState<Hit[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<Hit | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!value) {
      setSelected(null);
      return;
    }
    if (selected?.id === value) return;
    const cached = hits.find((h) => h.id === value);
    if (cached) setSelected(cached);
  }, [value, hits, selected]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (query.trim().length < 3) {
      setHits([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const data = await apiFetch<Hit[]>(
          `/partners/lookup?q=${encodeURIComponent(query.trim())}`,
        );
        setHits(data);
      } catch {
        setHits([]);
      } finally {
        setLoading(false);
      }
    }, 200);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          disabled={disabled}
          aria-invalid={error}
          className="h-9 w-full justify-between font-normal"
        >
          <span
            className={selected ? "text-foreground" : "text-muted-foreground"}
          >
            {selected?.company_name ??
              (value ? value.slice(0, 8) + "…" : "Search for a partner…")}
          </span>
          <ChevronsUpDown className="h-3.5 w-3.5 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-0"
        align="start"
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Type at least 3 letters…"
            value={query}
            onValueChange={setQuery}
          />
          <CommandList>
            {loading ? (
              <div className="flex items-center justify-center gap-2 px-4 py-6 text-sm text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Searching…
              </div>
            ) : query.trim().length < 3 ? (
              <CommandEmpty>Type at least 3 letters to search.</CommandEmpty>
            ) : hits.length === 0 ? (
              <CommandEmpty>No partners found.</CommandEmpty>
            ) : (
              <CommandGroup>
                {hits.map((h) => (
                  <CommandItem
                    key={h.id}
                    value={h.id}
                    onSelect={() => {
                      setSelected(h);
                      onChange(h.id);
                      setOpen(false);
                    }}
                  >
                    {h.company_name}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
